import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useResourcesContext, useEmpresa } from "app/contexts";
import { coordenadaRegex } from "app/utils/regexps";
import { Select } from "app/components/select";
import { Option } from "app/components/option";
import { Textarea } from "app/components/textarea";
import { Input } from "app/components/input";
import { Checkbox } from "app/components/checkbox";
import { DFlexResponsive } from "app/components/d-flex-responsive";
import { Container } from "app/components/container";
import { Row } from "app/components/row";
import { Column } from "app/components/column";
import { CardFooter } from "app/components/card-footer";
import { Button } from "app/components/button";
import { ConditionalButton } from "app/components/buttons";
import { CardBody } from "app/components/card-body";
import { CardHeader } from "app/components/card-header";
import { RouteError } from "app/components/route-error";

import toast from "react-hot-toast";

const initialFormState = {
  departamento: {
    departamento_id: "",
    ciudades: [],
  },
  establecimiento: {
    ciudad_id: 1,
    establecimiento_direccion_detallada: "",
    establecimiento_coordenadas: "",
    establecimiento_principal: false,
  },
  error: null,
};

export function EstablecimientoForm() {
  const params = useParams();
  const navigate = useNavigate();

  const {
    departamentos,
    getDepartamento,
    getDepartamentoByCiudadId,
    getCiudad,
  } = useResourcesContext();
  const { addEstablecimiento, getEstablecimiento, updateEstablecimiento } =
    useEmpresa();

  const [state, setState] = useState(initialFormState);

  useEffect(() => {
    try {
      if (!!params.index) {
        const establecimiento = getEstablecimiento(parseInt(params.index));

        const departamento = getDepartamentoByCiudadId(
          establecimiento.ciudad_id
        );

        if (departamento) {
          setState((state) => ({
            ...state,
            departamento,
            establecimiento,
          }));
        }
      } else {
        const departamento = getDepartamentoByCiudadId(
          state.establecimiento.ciudad_id
        );

        // const ciudad = getCiudad(state.establecimiento.ciudad_id);

        setState((state) => ({
          ...state,
          departamento,
        }));
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error,
      }));
    }
  }, [params.index, getDepartamentoByCiudadId, getEstablecimiento, getCiudad]);

  function handleSubmit(e) {
    e.preventDefault();

    try {
      if (!!params.index) {
        const updated = updateEstablecimiento(
          parseInt(params.index),
          state.establecimiento
        );

        if (!!updated) {
          toast.success("¡Establecimiento actualizado!");

          return navigate("../");
        }
      } else {
        const added = addEstablecimiento(state.establecimiento);

        if (!!added) {
          toast.success("¡Establecimiento agregado!");

          return navigate("../");
        }
      }
    } catch (error) {
      return toast.error(error.message);
    }
  }

  if (!!state.error) {
    return <RouteError message={state.error.message} navigateTo="../" />;
  }

  return (
    <div className="col-sm-12 col-xl-6 offset-xl-3">
      <form className="card" onSubmit={handleSubmit}>
        <CardHeader
          header={`${!!params.index ? "Editar " : "Agregar"} establecimiento`}
        />

        <CardBody>
          <Container>
            <Row>
              <Column>
                <Select
                  placeholder="Departamento"
                  value={state.departamento.departamento_id}
                  onChange={(e) => {
                    const departamento = getDepartamento(
                      parseInt(e.target.value)
                    );

                    setState((state) => ({ ...state, departamento }));
                  }}
                >
                  {departamentos.map((departamento) => (
                    <Option
                      key={departamento.departamento_id}
                      value={departamento.departamento_id}
                      title={departamento.departamento_nombre}
                    />
                  ))}
                </Select>
              </Column>

              <Column>
                <Select
                  placeholder="Municipio"
                  value={state.establecimiento.ciudad_id}
                  onChange={(e) =>
                    setState((state) => ({
                      ...state,
                      establecimiento: {
                        ...state.establecimiento,
                        ciudad_id: parseInt(e.target.value),
                      },
                    }))
                  }
                  disabled={!state.departamento.departamento_id}
                >
                  {state.departamento.ciudades.map(
                    ({ ciudad_id, ciudad_nombre }) => (
                      <Option
                        key={ciudad_id}
                        value={ciudad_id}
                        title={ciudad_nombre}
                      />
                    )
                  )}
                </Select>
              </Column>

              <Column>
                <Textarea
                  placeholder="Dirección detallada"
                  value={
                    state.establecimiento.establecimiento_direccion_detallada
                  }
                  onChange={(e) =>
                    setState((state) => ({
                      ...state,
                      establecimiento: {
                        ...state.establecimiento,
                        establecimiento_direccion_detallada:
                          e.target.value.trimStart(),
                      },
                    }))
                  }
                  autoFocus
                />
              </Column>

              <Column>
                <Input
                  placeholder="Coordenadas (opcional)"
                  value={state.establecimiento.establecimiento_coordenadas}
                  onChange={(e) =>
                    setState((state) => ({
                      ...state,
                      establecimiento: {
                        ...state.establecimiento,
                        establecimiento_coordenadas: e.target.value.trimStart(),
                      },
                    }))
                  }
                  pattern={coordenadaRegex.source}
                  title="Ejemplo: 15.783518367213972, -86.76581184662624"
                  required={false}
                />
              </Column>

              <Column>
                <Checkbox
                  label="¿Es un establecimiento principal?"
                  checked={state.establecimiento.establecimiento_principal}
                  onChange={(e) =>
                    setState((state) => ({
                      ...state,
                      establecimiento: {
                        ...state.establecimiento,
                        establecimiento_principal: e.target.checked,
                      },
                    }))
                  }
                />
              </Column>
            </Row>
          </Container>
        </CardBody>

        <CardFooter>
          <DFlexResponsive justifyContent="end">
            <ConditionalButton
              type="submit"
              condition={!params.index}
              titleWhenTrue="Agregar"
              titleWhenFalse="Actualizar"
            />

            <Button
              color="secondary"
              outline
              title="Cancelar"
              onClick={() => navigate("../")}
            />
          </DFlexResponsive>
        </CardFooter>
      </form>
    </div>
  );
}
